/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import { useState, useEffect, useRef } from "react";

// react-router components
import { useLocation, Link } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";

// Vision UI Dashboard React example components
import NotificationItem from "examples/Items/NotificationItem";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Vision UI Dashboard React context
import {
  useVisionUIController,
  setTransparentNavbar,
  setMiniSidenav,
} from "context";

// Utils
import auth from "utils/auth";

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useVisionUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar } = controller;
  const [openUserMenu, setOpenUserMenu] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);
  const currentUser = auth.getCurrentUser();
  const lastTransparentValue = useRef(transparentNavbar);
  const isUpdatingRef = useRef(false);

  // Update ref when transparentNavbar changes externally (only sync, don't trigger updates)
  // Use a separate effect that doesn't cause re-renders
  useEffect(() => {
    // Only update ref if we're not in the middle of updating transparentNavbar ourselves
    if (!isUpdatingRef.current) {
      lastTransparentValue.current = transparentNavbar;
    }
  }, [transparentNavbar]);

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }
  }, [fixedNavbar]);

  useEffect(() => {
    let timeoutId = null;
    let rafId = null;
    let mounted = true;
    
    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      // Don't do anything if component is unmounted or we're updating
      if (!mounted || isUpdatingRef.current) {
        return;
      }

      const shouldBeTransparent = (fixedNavbar && window.scrollY === 0) || !fixedNavbar;
      
      // Only update if value actually changed to prevent infinite loops
      if (lastTransparentValue.current !== shouldBeTransparent) {
        lastTransparentValue.current = shouldBeTransparent;
        isUpdatingRef.current = true;
        
        // Clear any pending timeout
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        
        // Use setTimeout to avoid calling setState during render
        timeoutId = setTimeout(() => {
          if (mounted) {
            setTransparentNavbar(dispatch, shouldBeTransparent);
            // Reset flag after a short delay
            setTimeout(() => {
              if (mounted) {
                isUpdatingRef.current = false;
              }
            }, 50);
          }
        }, 0);
      }
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar, { passive: true });

    // Call the handleTransparentNavbar function to set the state with the initial value.
    // Use requestAnimationFrame to ensure it runs after render
    rafId = requestAnimationFrame(() => {
      if (mounted) {
        handleTransparentNavbar();
      }
    });

    // Remove event listener on cleanup
    return () => {
      mounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      window.removeEventListener("scroll", handleTransparentNavbar);
      isUpdatingRef.current = false;
    };
  }, [dispatch, fixedNavbar]); // Removed transparentNavbar from dependencies to prevent infinite loops

  const handleMiniSidenav = () => {
    // On mobile, toggle sidenav (show/hide)
    // On desktop, toggle mini state
    if (window.innerWidth < 1200) {
      setMiniSidenav(dispatch, !miniSidenav);
    } else {
      setMiniSidenav(dispatch, !miniSidenav);
    }
  };
  const handleOpenUserMenu = (event) => setOpenUserMenu(event.currentTarget);
  const handleCloseUserMenu = () => setOpenUserMenu(false);
  const handleLogout = () => {
    auth.logout();
  };

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <VuiBox 
          color="inherit" 
          mb={{ xs: 1, md: 0 }} 
          sx={(theme) => navbarRow(theme, { isMini })}
          display="flex"
          alignItems="center"
          gap={2}
          width="100%"
        >
          <VuiBox
            component="button"
            onClick={handleMiniSidenav}
            onMouseEnter={() => {
              // Show sidenav when hovering over logo (only on desktop)
              if (window.innerWidth >= 1200 && miniSidenav) {
                setMiniSidenav(dispatch, false);
              }
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: { xs: "34px", sm: "40px", md: "40px" },
              height: { xs: "34px", sm: "40px", md: "40px" },
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                background: "rgba(255, 255, 255, 0.15)",
                transform: "scale(1.05)",
              },
            }}
          >
            <VuiBox
              component="img"
              src="https://www.audome.io/audome-uploads/icon.png"
              alt="AudoChat"
              sx={{
                width: { xs: "18px", sm: "24px", md: "24px" },
                height: { xs: "18px", sm: "24px", md: "24px" },
                objectFit: "contain",
              }}
            />
          </VuiBox>
        </VuiBox>
        {isMini ? null : (
          <VuiBox 
            sx={(theme) => navbarRow(theme, { isMini })}
            display={{ xs: "none", md: "flex" }}
          >
            <VuiBox color={light ? "white" : "inherit"}>
              {currentUser ? (
                <>
                  <IconButton
                    sx={navbarIconButton}
                    size="small"
                    onClick={handleOpenUserMenu}
                  >
                    <Icon
                      sx={({ palette: { dark, white } }) => ({
                        color: light ? white.main : dark.main,
                      })}
                    >
                      account_circle
                    </Icon>
                    <VuiTypography
                      variant="button"
                      fontWeight="medium"
                      color={light ? "white" : "dark"}
                      sx={{ ml: 0.5 }}
                    >
                      {currentUser.name}
                    </VuiTypography>
                  </IconButton>
                  <Menu
                    anchorEl={openUserMenu}
                    open={Boolean(openUserMenu)}
                    onClose={handleCloseUserMenu}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    <NotificationItem
                      color="text"
                      image={
                        <Icon fontSize="small" sx={{ color: ({ palette: { white } }) => white.main }}>
                          person
                        </Icon>
                      }
                      title={[currentUser.name, currentUser.email]}
                      date={currentUser.role === "admin" ? "Admin" : "Agent"}
                      onClick={handleCloseUserMenu}
                    />
                    <NotificationItem
                      color="text"
                      image={
                        <Icon fontSize="small" sx={{ color: ({ palette: { white } }) => white.main }}>
                          logout
                        </Icon>
                      }
                      title={["", "Logout"]}
                      onClick={handleLogout}
                    />
                  </Menu>
                </>
              ) : (
                <Link to="/authentication/sign-in">
                  <IconButton sx={navbarIconButton} size="small">
                    <Icon
                      sx={({ palette: { dark, white } }) => ({
                        color: light ? white.main : dark.main,
                      })}
                    >
                      account_circle
                    </Icon>
                    <VuiTypography
                      variant="button"
                      fontWeight="medium"
                      color={light ? "white" : "dark"}
                    >
                      Sign in
                    </VuiTypography>
                  </IconButton>
                </Link>
              )}
            </VuiBox>
          </VuiBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
